import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import _ from '@sparta.global.vendors/lodash';

/**
 * @name        SpartaLazyLoadUtility
 * @file        sparta-lazy-load-utility.js
 * @description Utility to lazy load images
 * @author      Yelena Rose
 * @copyright   © 2018 Bank of America Corporation. All rights reserved.
 */

const SPARTA_GLOBAL_READY = 'SPARTA_GLOBAL_READY.spartaBootstrapUtility';
const IN_VIEW_THRESHOLD = 50;
const THROTTLE_TIME = 300;
const SLOW_NETWORK_TIME = 2000;
const SLOW_CONNECTION_TYPE = '2g';
const EVENTS_TO_THROTTLE = [[document, 'scroll'], [document, 'touchmove'], [window, 'resize'], [window, 'orientationchange']];
const CLASSES = {
  image: 'spa-lazy-image',
  background: 'spa-lazy-background',
  loaded: 'loaded',
  skipSlow: 'spa-skip-slow'
};
const IMAGES = 'lazyImages';
const BACKGROUNDS = 'lazyBackgrounds';

/**
 * @class SpartaLazyLoadUtility
 */
class SpartaLazyLoadUtility {
  /**
   * @method constructor
   * @description initializes new instance of SpartaLazyLoadUtility Class
   */
  constructor() {
    this.isFirstLoad = true;
    this.isSlowNetwork = false;
    this.isNavigatorConnectionSupported = false;
    this.isIntersectionObserverSupported = false;
    this.isNavigatorConnectionSupported = 'connection' in navigator;
    this.isIntersectionObserverSupported = 'IntersectionObserver' in window;
    this.addLazyLoadingOnPageLoad();
  }

  /**
   * `spartaLazyLoadUtility.init()` can be called to lazy load dynamic images (from PCM)
   *
   * @testExample init()
   * @testExpect this.isFirstLoad toEqual false
   *
   * @mockVariable this isFirstLoad true
   *
   * @returns {void}
   */
  init() {
    this.getEachImageType();
    this.startLoadingProcess();
    this.isFirstLoad = false;
  }

  /**
   * Load lazy static images on page load if `disableLazyLoadOnInit` site param
   * or `disableLazyLoadOnInit` page param is not set to true - page level param
   * overwrites site level param. This parameter can be set to true when you
   * need to wait for a dynamic content to load before loading images.
   *
   * @testExample addLazyLoadingOnPageLoad()
   * @testExpect this.isFirstLoad toEqual true
   *
   * @mockSpaParams { siteParams: { disableLazyLoadOnInit: true },
   * pageParams: { disableLazyLoadOnInit: true }}
   * @mockVariable this isFirstLoad true
   *
   * @returns {void}
   */
  addLazyLoadingOnPageLoad() {
    const disableLazyLoadOnInit = spaParams.pageParams.disableLazyLoadOnInit !== undefined ? spaParams.pageParams.disableLazyLoadOnInit : spaParams.siteParams.disableLazyLoadOnInit;
    if (!disableLazyLoadOnInit) {
      spartaEventsUtility.sub(SPARTA_GLOBAL_READY, () => {
        this.getEachImageType();
        this.startLoadingProcess();
      });
      this.isFirstLoad = false;
    }
  }

  /**
   * Get regular images and background images with lazy load class
   *
   * @returns {void}
   */
  getEachImageType() {
    this[IMAGES] = this.getLazyLoadImages();
    this[BACKGROUNDS] = [...document.querySelectorAll(`.${CLASSES.background}:not(.${CLASSES.loaded})`)];
  }

  /**
   * Start Lazy Loading - loads differently based on IntersectionObserver support
   *
   * @returns {void}
   */
  startLoadingProcess() {
    if (this.isIntersectionObserverSupported) {
      this.loadEachImageTypeWithObserver(IMAGES, CLASSES.image);
      this.loadEachImageTypeWithObserver(BACKGROUNDS, CLASSES.background);
    } else {
      // need to set `throttled` here so it can be removed later
      this.throttled = _.throttle(this.throttleLazyLoad.bind(this), THROTTLE_TIME);
      this.loadBasedOnNavigatorConnectionSupport(this.loadWithEventsFallback);
    }
  }

  /**
   * Load regular or background images with IntersectionObserver
   *
   * @param {string} imageType - image or background
   * @param {string} className - lazy load class name
   * @returns {void}
   */
  loadEachImageTypeWithObserver(imageType, className) {
    if (this[imageType].length > 0) {
      this.loadBasedOnNavigatorConnectionSupport(this.loadWithIntersectionObserver.bind(this, imageType, className));
    }
  }

  /**
   * Load based on navigator.connection support
   *
   * @testExample loadBasedOnNavigatorConnectionSupport()
   * @testExpect this.checkIfSlowNetwork.called toEqual true
   * @testExpect this.isFirstLoad toEqual false
   *
   * @spy this.checkIfSlowNetwork
   * @mockVariable this isNavigatorConnectionSupported false
   * @mockVariable this isFirstLoad true
   *
   * @param {Function} loadWithFunction -
   *    [loadWithIntersectionObserver || loadWithEventsFallback]
   * @returns {void}
   */
  loadBasedOnNavigatorConnectionSupport(loadWithFunction) {
    if (!this.isNavigatorConnectionSupported && this.isFirstLoad) {
      this.checkIfSlowNetwork().then(() => {
        loadWithFunction.call(this);
      });
      this.isFirstLoad = false; // set to false here to prevent double calling
      // of the checkIfSlowNetwork()
    } else if (!this.isNavigatorConnectionSupported && !this.isFirstLoad) {
      loadWithFunction.call(this);
    } else {
      this.isSlowNetwork = this.isNavigatorConnectionSupported && navigator.connection.effectiveType === SLOW_CONNECTION_TYPE;
      loadWithFunction.call(this);
    }
  }

  /**
   * Find ready for lazy loading images
   *
   * @testExample getLazyLoadImages()
   * @testOutput notInclude mockedElements[0]
   *
   * @testExample getLazyLoadImages()
   * @testOutput notInclude mockedElements[1]
   *
   * @testExample getLazyLoadImages()
   * @testOutput include mockedElements[2]
   *
   * @mockElement img[src="image.jpg"]
   * @mockElement img.spa-lazy-image[src="image.jpg"]
   * @mockElement img.spa-lazy-image[data-src="image.jpg"]
   *
   * @returns {Array} array of image elements with lazy load class
   */
  getLazyLoadImages() {
    const lazyImageClass = `img.${CLASSES.image}`;
    let lazyElements = [...document.querySelectorAll(lazyImageClass)];
    // remove any elements that don't have data-src attribute (not ready dynamic images)
    if (lazyElements.length > 0) {
      lazyElements = lazyElements.filter(element => !this.isEmptyDataSrc(element));
    }
    return lazyElements;
  }

  /**
   * Check if 'data-src' attribute is empty or doesn't exist
   * in image or picture source
   *
   * @testExample isEmptyDataSrc(mockedElements[0])
   * @testOutput equal true
   *
   * @testExample isEmptyDataSrc(mockedElements[1])
   * @testOutput equal true
   *
   * @testExample isEmptyDataSrc(mockedElements[2])
   * @testOutput equal false
   *
   * @testExample isEmptyDataSrc(mockedElements[3])
   * @testOutput equal true
   *
   * @mockElement img.spa-lazy-image[src="image.jpg"]
   * @mockElement img.spa-lazy-image[src="image.jpg"][data-src=""]
   * @mockElement img.spa-lazy-image[src="image.jpg"][data-src="image.jpg"]
   * @mockElement div
   *
   * @param {Object} element - image DOM element
   * @returns {Boolean} true if data-src is missing for any of the 'source' elements or img src
   */
  isEmptyDataSrc(element) {
    if (element.tagName === 'IMG') {
      const parentElement = element.parentNode;
      if (parentElement.tagName === 'PICTURE') {
        const srcset = [...parentElement.querySelectorAll('source')];
        return this.isEmptyDataSrcForPictureSource(srcset);
      }
      // check for img tag
      return SpartaLazyLoadUtility.isEmptyDataSrcAttribute(element);
    }
    return true;
  }

  /**
   * Check if 'data-src' attribute is empty or doesn't exist
   * in picture source element
   *
   * @testExample isEmptyDataSrcForPictureSource(mockedElements)
   * @testOutput equal true
   *
   * @testExample isEmptyDataSrcForPictureSource(mockedElements[0])
   * @testOutput equal false
   *
   * @testExample isEmptyDataSrcForPictureSource(mockedElements[2])
   * @testOutput equal true
   *
   * @mockElement source[data-src="image.jpg"]
   * @mockElement source
   * @mockElement source[data-src=""]
   *
   * @param {Object} srcset - srcset of source from picture DOM element
   * @returns {Boolean} true if data-src is missing for any of the 'source' elements or img src
   */
  isEmptyDataSrcForPictureSource(srcset) {
    return Array.isArray(srcset) ? srcset.some(src => SpartaLazyLoadUtility.isEmptyDataSrcAttribute(src)) : SpartaLazyLoadUtility.isEmptyDataSrcAttribute(srcset);
  }

  /**
   * Check if `data-src` attributes is empty
   *
   * @param {object} element - img or source
   * @returns {boolean} true if empty
   */
  static isEmptyDataSrcAttribute(element) {
    return element.getAttribute('data-src') === '' || !element.hasAttribute('data-src');
  }

  /**
   * Load images and adds lazy loading to the view changing events
   *
   * @returns {void}
   */
  loadWithEventsFallback() {
    this.lazyLoadImages(IMAGES, CLASSES.image);
    this.lazyLoadImages(BACKGROUNDS, CLASSES.background);
    EVENTS_TO_THROTTLE.forEach(item => item[0].addEventListener(item[1], this.throttled));
  }

  /**
   * Throttle lazy loading
   *
   * @returns {void}
   */
  throttleLazyLoad() {
    this.lazyLoadImages(IMAGES, CLASSES.image);
    this.lazyLoadImages(BACKGROUNDS, CLASSES.background);
    // remove events listeners after all images are loaded
    if (this[IMAGES].length === 0 && this[BACKGROUNDS].length.length === 0) {
      EVENTS_TO_THROTTLE.forEach(item => item[0].removeEventListener(item[1], this.throttled));
    }
  }

  /**
   * Load picture helper images with Intersection Observer when in view
   *
   * @param {string} imageType - image or background
   * @param {string} className - lazy load class name
   * @returns {void}
   */
  loadWithIntersectionObserver(imageType, className) {
    const options = {
      root: null,
      rootMargin: `${IN_VIEW_THRESHOLD}px`
    };
    const lazyImageObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target;
          const skipLoading = this.isSlowNetwork && lazyImage.classList.contains(CLASSES.skipSlow);
          if (className === CLASSES.image && !skipLoading) {
            this.lazyLoad(lazyImage);
            lazyImage.classList.remove(CLASSES.image);
          } else if (className === CLASSES.background && !skipLoading) {
            lazyImage.classList.add(CLASSES.loaded);
          }
          lazyImageObserver.unobserve(lazyImage);
        }
      });
    }, options);
    if (this[imageType].length > 0) {
      this[imageType].forEach(lazyImage => {
        lazyImageObserver.observe(lazyImage);
      });
    }
  }

  /**
   * Lazy load images
   *
   * @param {string} imageType - image or background
   * @param {string} className - lazy load class name
   * @returns {void}
   */
  lazyLoadImages(imageType, className) {
    requestAnimationFrame(() => {
      this[imageType].forEach(lazyImage => {
        if (this.isInView(lazyImage)) {
          if (className === CLASSES.image) {
            this.lazyLoad(lazyImage);
          } else if (className === CLASSES.background && !(this.isSlowNetwork && lazyImage.classList.contains(CLASSES.skipSlow))) {
            lazyImage.classList.add(CLASSES.loaded);
          }
          // once image is loaded, we don't need to preload it again
          lazyImage.classList.remove(className);
          this[imageType] = this[imageType].filter(element => element !== lazyImage);
        }
      });
    });
  }

  /**
   * Load an image by placing data-src content into src
   *
   * @testExample lazyLoad(mockedElements[0])
   * @testExpect this.replaceSrc.called toEqual true
   *
   * @testExample lazyLoad(mockedElements[1])
   * @testExpect this.replaceSrc.called toEqual false
   *
   * @spy this.replaceSrc
   * @mockElement img[src="placeholder.jpg"][data-src="image.jpg"]
   * @mockElement div
   *
   * @param {Object} element image DOM element
   * @returns {void}
   */
  lazyLoad(element) {
    if (element.tagName === 'IMG') {
      const skipSlow = element.classList.contains(CLASSES.skipSlow) || element.dataset.skipSlow;
      const parentElement = element.parentNode;
      if (parentElement.tagName === 'PICTURE') {
        const sources = [...parentElement.querySelectorAll('source')];
        sources.forEach(source => {
          this.replaceSrc(source, 'srcset', skipSlow);
        });
        // if picture tag is not supported (IE), then trigger picturefill
        if (!window.HTMLPictureElement) {
          window.picturefill({
            reevaluate: true,
            elements: [element]
          });
        }
      } else {
        // img tag without srcset
        this.replaceSrc(element, 'src', skipSlow);
        // TODO add support for img tag with srcset
      }
    }
  }

  /**
   * Replace image source attribute with data-src and removes data-src
   *
   * @testExample replaceSrc(mockedElements[0], "srcset", false)
   * @testExpect mockedElements[0].srcset toEqual "image.jpg"
   *
   * @testExample replaceSrc(mockedElements[1], "srcset", false)
   * @testExpect mockedElements[1].srcset toEqual "placeholder.jpg"
   *
   * @testExample replaceSrc(mockedElements[2], "srcset", false)
   * @testExpect mockedElements[2].srcset toEqual "placeholder.jpg"
   *
   * @testExample replaceSrc(mockedElements[3], "src", false)
   * @testExpect mockedElements[3].src include "image.jpg"
   *
   * @mockElement source[srcset="placeholder.jpg"][data-src="image.jpg"]
   * @mockElement source[srcset="placeholder.jpg"][data-src=""]
   * @mockElement source[srcset="placeholder.jpg"]
   * @mockElement img[src="placeholder.jpg"][data-src="image.jpg"]
   * @mockDelay 1000
   *
   * @param {Object} element - [source || img]
   * @param {String} attr - [srcset || src]
   * @param {Boolean} skipSlow - if true, image will not be loaded on slow network
   * @returns {void}
   */
  replaceSrc(element, attr, skipSlow) {
    if (!(this.isSlowNetwork && skipSlow) && 'src' in element.dataset && element.dataset.src !== '') {
      element.setAttribute(attr, element.dataset.src);
      element.removeAttribute('data-src');
    }
  }

  /**
   * Determine if the network speed is slow - fallback for navigator.connection.effectiveType
   * @description It should be called only once only to set this.isSlowNetwork
   * Network speed is just an estimate in this case
   *
   * @testExample checkIfSlowNetwork()
   * @testOutput true
   * @testTrigger onerror mockedElements[0]
   * @testExpect this.isSlowNetwork equal false
   *
   * @mockElement img[src="https://www-dev1.ecnp.bankofamerica.com/credit-cards/spa-assets/images/not-existing-image.png"]
   *
   * @returns {Promise} get resolved when image is loaded
   */
  async checkIfSlowNetwork() {
    const testImageUrl = spaParams.global.en.smallImage;
    const startTime = new Date().getTime();
    let downloadSpeed = 0;
    return new Promise(resolve => {
      const imageToLoad = new Image();
      const noCacheParam = `?time=${startTime}`;
      imageToLoad.src = testImageUrl + noCacheParam;
      imageToLoad.onload = () => {
        const endTime = new Date().getTime();
        downloadSpeed = endTime - startTime;
        this.isSlowNetwork = downloadSpeed > SLOW_NETWORK_TIME;
        // console.log(downloadSpeed, ' Speed');
        resolve(true);
      };
      imageToLoad.onerror = () => {
        // if there's an image loading error, don't stop execution
        // and use images for the fast network
        resolve(false);
      };
    });
  }

  /**
   * Return true when blurry image is in view minus the IN_VIEW_THRESHOLD
   * IN_VIEW_THRESHOLD allows to loading process to start before before
   * the images is in view
   *
   * @param {Object} lazyImage - DOM img element
   * @return {boolean} true if in view
   */
  isInView(lazyImage) {
    const computedStyle = getComputedStyle(lazyImage);
    const boundingClientRect = lazyImage.getBoundingClientRect();
    return boundingClientRect.top <= window.innerHeight + IN_VIEW_THRESHOLD && boundingClientRect.bottom >= -IN_VIEW_THRESHOLD && computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
  }
}
var index = new SpartaLazyLoadUtility();

export { index as default };
